import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  ListItemIcon, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import { Handle, Position, ViewportPortal, useUpdateNodeInternals } from '@xyflow/react';
import { formatTokenCount } from '../../Utils/workflowTokenUsage';
import { buildActionLogicalGroupBounds, collapsedLogicalGroupSize } from './actionFlowLogicalGroups';
import useLogicalGroupDragging from './useLogicalGroupDragging';

const EMPTY_COLLAPSED_GROUPS = new Set();
export const CollapsedLogicalGroupAnchor = ({ id, data }) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const handles = data?.connectionHandles || [];
  const signature = handles.map((handle) => [handle.id, handle.x, handle.y, handle.position].join(':')).join('|');
  useEffect(() => updateNodeInternals(id), [id, signature, updateNodeInternals]);
  return <>
    <Handle type="target" id="target" position={Position.Left} style={{ opacity: 0, pointerEvents: 'none' }} />
    <Handle type="source" id="source" position={Position.Right} style={{ opacity: 0, pointerEvents: 'none' }} />
    {handles.map((handle) => <Handle key={handle.id} id={handle.id} type={handle.type}
      position={handle.position} isConnectable={false} style={{ opacity: 0, pointerEvents: 'none',
        left: handle.x, top: handle.y, right: 'auto', bottom: 'auto', transform: 'translate(-50%, -50%)' }} />)}
  </>;
};

const logicalGroupMenuPaperSx = {
  minWidth: 232,
  maxWidth: 'calc(100vw - 24px)',
  p: 0.75,
  border: '1px solid rgba(87, 87, 176, 0.14)',
  borderRadius: '14px',
  backgroundColor: '#fff',
  backgroundImage: 'none',
  boxShadow: '0 14px 34px rgba(39, 40, 84, 0.16), 0 3px 9px rgba(39, 40, 84, 0.08)',
};

const logicalGroupMenuItemSx = {
  minHeight: 44,
  px: 1,
  py: 0.5,
  borderRadius: '10px',
  color: '#292941',
  '&:hover': { backgroundColor: '#f5f4ff' },
  '&.Mui-focusVisible': {
    backgroundColor: '#f2f1ff',
    boxShadow: 'inset 0 0 0 1px rgba(87, 87, 176, 0.26)',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 32,
    width: 32,
    height: 32,
    mr: 1.25,
    borderRadius: '9px',
    display: 'grid',
    placeItems: 'center',
    color: '#5557a8',
    backgroundColor: '#f1f0ff',
    '& .MuiSvgIcon-root': { fontSize: 18 },
  },
  '& .MuiListItemText-root': { my: 0 },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
};

const ActionFlowLogicalGroupFrames = ({ nodes, canEdit = false, onRenameGroup, onEditGroup,
  onSetGroupEnabled, onRunGroup, pendingRunGroupId = null, pendingGroupId = null, nodeType = 'action', t,
  groups: suppliedGroups, collapsedIds = EMPTY_COLLAPSED_GROUPS, onToggleCollapse,
  canMove = false, getZoom, onGroupDragStart, onGroupDrag, onGroupDragEnd, onGroupNudge,
  tokenSummaries, locale }) => {
  const groups = useMemo(() => suppliedGroups || buildActionLogicalGroupBounds(nodes, nodeType), [suppliedGroups, nodes, nodeType]);
  const dragHandlers = useLogicalGroupDragging({ disabled: !canMove, zoom: getZoom,
    onStart: onGroupDragStart, onMove: onGroupDrag, onEnd: onGroupDragEnd });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuGroup, setMenuGroup] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const memberEnabled = (member) => member.node.isEnabled
    ?? [true, 1, '1', 'true', 'True'].includes(member.node.item?.enable);
  const allEnabled = Boolean(menuGroup?.members.length) && menuGroup.members.every(memberEnabled);
  const allDisabled = Boolean(menuGroup?.members.length) && menuGroup.members.every((member) => !memberEnabled(member));

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuGroup(null);
  };
  const openGroupDialog = (mode, group) => {
    setActiveGroup(group);
    setActiveDialog(mode);
    if (mode === 'rename') setEditedTitle(group.title);
    closeMenu();
  };
  const closeDialog = () => {
    setActiveDialog(null);
    setActiveGroup(null);
  };
  const saveRename = (event) => {
    event.preventDefault();
    const title = editedTitle.trim();
    if (!activeGroup || !title || typeof onRenameGroup !== 'function') return;
    onRenameGroup(activeGroup.id, title);
    closeDialog();
  };

  if (!groups.length && !activeDialog) return null;
  return (
    <>
      <ViewportPortal>
        {groups.map((group) => {
          const collapsed = collapsedIds.has(group.id);
          const size = collapsed ? collapsedLogicalGroupSize(group) : group;
          const tokenSummary = collapsed ? tokenSummaries?.get(group.id) : null;
          const amount = (value) => value == null ? '—' : formatTokenCount(value, locale);
          return (
          <Box key={group.id} role="group" aria-label={group.title}
            data-testid="dag-action-logical-group" data-group-id={group.id}
            sx={{
              position: 'absolute', left: group.x, top: group.y,
              width: size.width, height: size.height, boxSizing: 'border-box',
              border: '2px dashed rgba(87, 87, 176, 0.55)', borderRadius: '22px',
              backgroundColor: collapsed ? '#f5f4fc' : 'rgba(87, 87, 176, 0.035)', pointerEvents: 'none',
            }}>
            <Box className="nodrag nopan" data-testid="dag-logical-group-drag-handle"
              tabIndex={canMove ? 0 : undefined}
              aria-label={canMove ? t('dag.logical_group_move', 'Move group: {{title}}', { title: group.title }) : undefined}
              onPointerDown={(event) => dragHandlers.onPointerDown(group, event)}
              onPointerMove={dragHandlers.onPointerMove} onPointerUp={dragHandlers.onPointerUp}
              onPointerCancel={dragHandlers.onPointerCancel} onLostPointerCapture={dragHandlers.onLostPointerCapture}
              onKeyDown={(event) => {
                dragHandlers.onKeyDown(event);
                if (event.target !== event.currentTarget || !canMove) return;
                const step = event.shiftKey ? 100 : 20;
                const delta = { ArrowLeft: { x: -step, y: 0 }, ArrowRight: { x: step, y: 0 },
                  ArrowUp: { x: 0, y: -step }, ArrowDown: { x: 0, y: step } }[event.key];
                if (!delta) return;
                event.preventDefault(); event.stopPropagation();
                onGroupNudge?.(group, delta);
              }} sx={{
              position: 'absolute', top: 8, left: 18, right: 12, m: 0,
              pointerEvents: 'auto', minHeight: 34, touchAction: canMove ? 'none' : 'auto',
              cursor: canMove ? 'grab' : 'default',
              '&:active': { cursor: canMove ? 'grabbing' : 'default' },
              '&:focus-visible': { outline: '2px solid #5757b0', outlineOffset: 3, borderRadius: 1 },
            }}>
              <Tooltip title={<><Typography variant="body2" sx={{ fontWeight: 700 }}>{group.title}</Typography>
                {tokenSummary && <Box sx={{ minWidth: 230, mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,.16)' }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                    {t('workflow.ai_token_usage_title', 'AI token usage')}
                  </Typography>
                  {[
                    ['dag.logical_group_token_input', 'Input (first action)', tokenSummary.input],
                    ['dag.logical_group_token_output', 'Output (last action)', tokenSummary.output],
                    ['dag.logical_group_token_total', 'Total for the group', tokenSummary.total],
                  ].map(([key, fallback, value]) => <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{t(key, fallback)}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{amount(value)}</Typography>
                  </Box>)}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.7, opacity: 0.8 }}>
                    {t('dag.logical_group_token_latest', 'Latest available usage for each action; executions may differ.')}
                    {tokenSummary.partial && ` ${t('dag.logical_group_token_partial', 'Some actions have no recorded usage.')}`}
                    {tokenSummary.ambiguous && ` ${t('dag.logical_group_token_ambiguous', 'Entry or exit is not unique.')}`}
                  </Typography>
                </Box>}
                {canMove && <Box sx={{ mt: 0.5 }}>
                {t('dag.logical_group_move_hint', 'Drag this title to move the whole group, or focus it and use the arrow keys.')}
              </Box>}</>} arrow>
                <Typography component="h3" variant="subtitle2" className="nodrag nopan"
                  tabIndex={collapsed ? 0 : undefined}
                  sx={{
                    minWidth: 0, maxWidth: '100%', m: 0, pr: onToggleCollapse ? '76px' : '38px', fontWeight: 750,
                    lineHeight: '18px', color: '#45458e',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap',
                    cursor: canMove ? 'inherit' : 'help',
                  }}>
                  {group.title}
                </Typography>
              </Tooltip>
              {onToggleCollapse && <Tooltip title={t(collapsed ? 'dag.logical_group_expand' : 'dag.logical_group_collapse',
                collapsed ? 'Expand group' : 'Collapse to title')} arrow>
                <IconButton className="nodrag nopan" size="small" aria-expanded={!collapsed}
                  aria-label={t(collapsed ? 'dag.logical_group_expand' : 'dag.logical_group_collapse',
                    collapsed ? 'Expand group' : 'Collapse to title')}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => { event.stopPropagation(); onToggleCollapse(group.id); }}
                  sx={{ position: 'absolute', top: 0, right: 36, width: 30, height: 30,
                    color: '#45458e', backgroundColor: '#fff', border: '1px solid rgba(87,87,176,.16)', borderRadius: '10px' }}>
                  {collapsed ? <ExpandMoreRoundedIcon fontSize="small" /> : <ExpandLessRoundedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>}
              <Tooltip title={t('dag.logical_group_options', 'Group options')} arrow>
                <IconButton
                  className="nodrag nopan"
                  size="small"
                  aria-label={t('dag.logical_group_options', 'Group options')}
                  aria-haspopup="menu"
                  disabled={Boolean(pendingGroupId) || Boolean(pendingRunGroupId)}
                  onPointerDown={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuAnchorEl(event.currentTarget);
                    setMenuGroup(group);
                  }}
                  sx={{
                    position: 'absolute', top: 0, right: 0,
                    width: 30, height: 30, p: 0,
                    color: '#45458e',
                    backgroundColor: '#fff',
                    border: '1px solid rgba(87, 87, 176, 0.16)',
                    borderRadius: '10px',
                    boxShadow: '0 2px 8px rgba(69, 69, 142, 0.1)',
                    transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                      backgroundColor: '#f7f6ff',
                      borderColor: 'rgba(87, 87, 176, 0.3)',
                      boxShadow: '0 3px 10px rgba(69, 69, 142, 0.14)',
                    },
                    '&.Mui-focusVisible': {
                      outline: '3px solid rgba(87, 87, 176, 0.24)',
                      outlineOffset: 1,
                    },
                    '& .MuiSvgIcon-root': { fontSize: 18 },
                  }}
                >
                  {pendingGroupId === group.id || pendingRunGroupId === group.id
                    ? <CircularProgress size={16} /> : <MoreVertIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ); })}
      </ViewportPortal>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 0, sx: logicalGroupMenuPaperSx }}
        MenuListProps={{
          'aria-label': t('dag.logical_group_options', 'Group options'),
          disablePadding: true,
          sx: { display: 'flex', flexDirection: 'column', gap: '2px' },
        }}
      >
        {canEdit && typeof onRunGroup === 'function' && <>
          <MenuItem sx={logicalGroupMenuItemSx}
            disabled={Boolean(pendingGroupId) || Boolean(pendingRunGroupId) || allDisabled}
            onClick={() => { onRunGroup(menuGroup); closeMenu(); }}>
            <ListItemIcon><PlayArrowRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText secondary={t('dag.logical_group_run_hint')}>
              {t('dag.logical_group_run')}
            </ListItemText>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
        </>}
        {canEdit && typeof onSetGroupEnabled === 'function' && <>
          <MenuItem sx={logicalGroupMenuItemSx} disabled={Boolean(pendingGroupId) || allEnabled}
            onClick={() => { onSetGroupEnabled(menuGroup, true); closeMenu(); }}>
            <ListItemIcon><PowerSettingsNewRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('dag.logical_group_enable_all', 'Enable all actions')}</ListItemText>
          </MenuItem>
          <MenuItem sx={logicalGroupMenuItemSx} disabled={Boolean(pendingGroupId) || allDisabled}
            onClick={() => { onSetGroupEnabled(menuGroup, false); closeMenu(); }}>
            <ListItemIcon><PauseRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('dag.logical_group_disable_all', 'Disable all actions')}</ListItemText>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
        </>}
        {canEdit && typeof onEditGroup === 'function' && (
          <MenuItem
            sx={logicalGroupMenuItemSx}
            onClick={() => { onEditGroup(menuGroup); closeMenu(); }}
          >
            <ListItemIcon><AccountTreeOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('dag.logical_group_edit_logic', 'Modify logic')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          sx={logicalGroupMenuItemSx}
          onClick={() => openGroupDialog('prompt', menuGroup)}
        >
          <ListItemIcon><SubjectOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('dag.logical_group_view_prompt', 'View original request')}</ListItemText>
        </MenuItem>
        {canEdit && typeof onRenameGroup === 'function' && (
          <MenuItem
            sx={logicalGroupMenuItemSx}
            onClick={() => openGroupDialog('rename', menuGroup)}
          >
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('dag.logical_group_rename', 'Rename group')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={activeDialog === 'rename'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <Box component="form" onSubmit={saveRename}>
          <DialogTitle>{t('dag.logical_group_rename', 'Rename group')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label={t('dag.logical_group_name', 'Group name')}
              value={editedTitle}
              onChange={(event) => setEditedTitle(event.target.value)}
              inputProps={{ maxLength: 4000 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDialog}>{t('common.cancel', 'Cancel')}</Button>
            <Button type="submit" variant="contained" disabled={!editedTitle.trim()}>
              {t('common.save', 'Save')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={activeDialog === 'prompt'} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dag.logical_group_original_prompt', 'Original request')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {activeGroup?.originalPrompt || activeGroup?.title || ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ActionFlowLogicalGroupFrames;
